
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { notify } from '@/utils/notification';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Plus, Loader2 } from 'lucide-react';
import EventCard from './EventCard';
import EventForm from './EventForm';
import RegistrationList from './RegistrationList';

export interface GroupEvent {
  id: string;
  practitioner_id: string;
  title: string;
  title_en: string | null;
  title_zh: string | null;
  description: string;
  description_en: string | null;
  description_zh: string | null;
  event_date: string;
  end_date: string | null;
  duration_minutes: number;
  location_type: string;
  location_details: string | null;
  price: number;
  currency: string;
  max_participants: number;
  min_participants: number | null;
  cover_image_url: string | null;
  status: string;
  registration_deadline: string | null;
  tags: string[];
  service_track: string;
  is_recurring: boolean;
  recurring_pattern: any;
  created_at: string;
  updated_at: string;
  registration_count?: number;
}

const GroupEventsManager: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const [events, setEvents] = useState<GroupEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<GroupEvent | null>(null);
  const [viewingRegistrations, setViewingRegistrations] = useState<string | null>(null);

  const fetchEvents = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('group_events')
        .select('*')
        .eq('practitioner_id', user.id)
        .order('event_date', { ascending: false });

      if (error) throw error;

      // Fetch registration counts
      const eventIds = (data || []).map(e => e.id);
      let regCounts: Record<string, number> = {};
      if (eventIds.length > 0) {
        const { data: regs } = await supabase
          .from('group_event_registrations')
          .select('event_id')
          .in('event_id', eventIds)
          .in('status', ['registered', 'attended']);
        
        if (regs) {
          regs.forEach(r => {
            regCounts[r.event_id] = (regCounts[r.event_id] || 0) + 1;
          });
        }
      }

      setEvents((data || []).map(e => ({
        ...e,
        tags: e.tags || [],
        registration_count: regCounts[e.id] || 0,
      })));
    } catch (err) {
      console.error('Failed to fetch events:', err);
      notify.error(t('error'), t('events.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [user?.id, t]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const now = new Date().toISOString();
  const upcoming = events.filter(e => e.status === 'published' && e.event_date >= now);
  const past = events.filter(e => e.status === 'published' && e.event_date < now);
  const drafts = events.filter(e => e.status === 'draft');
  const cancelled = events.filter(e => e.status === 'cancelled');

  const handleEdit = (event: GroupEvent) => {
    setEditingEvent(event);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingEvent(null);
  };

  const handleFormSuccess = () => {
    handleFormClose();
    fetchEvents();
  };

  if (viewingRegistrations) {
    return (
      <RegistrationList
        eventId={viewingRegistrations}
        onBack={() => setViewingRegistrations(null)}
      />
    );
  }

  if (showForm) {
    return (
      <EventForm
        event={editingEvent}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">{t('events.title')}</h2>
        <p className="text-muted-foreground text-sm mt-1">{t('events.subtitle')}</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-16 space-y-4">
          <p className="text-muted-foreground">{t('events.noEvents')}</p>
          <Button onClick={() => setShowForm(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('events.createFirstEvent')}
          </Button>
        </div>
      ) : (
        <Tabs defaultValue="upcoming">
          <TabsList>
            <TabsTrigger value="upcoming">{t('events.upcoming')} ({upcoming.length})</TabsTrigger>
            <TabsTrigger value="drafts">{t('events.drafts')} ({drafts.length})</TabsTrigger>
            <TabsTrigger value="past">{t('events.past')} ({past.length})</TabsTrigger>
            {cancelled.length > 0 && (
              <TabsTrigger value="cancelled">{t('events.cancelled')} ({cancelled.length})</TabsTrigger>
            )}
          </TabsList>
          <TabsContent value="upcoming">
            <EventGrid events={upcoming} onEdit={handleEdit} onViewRegistrations={setViewingRegistrations} onRefresh={fetchEvents} emptyMessage={t('events.noUpcoming')} />
          </TabsContent>
          <TabsContent value="drafts">
            <EventGrid events={drafts} onEdit={handleEdit} onViewRegistrations={setViewingRegistrations} onRefresh={fetchEvents} emptyMessage={t('events.noDrafts')} />
          </TabsContent>
          <TabsContent value="past">
            <EventGrid events={past} onEdit={handleEdit} onViewRegistrations={setViewingRegistrations} onRefresh={fetchEvents} emptyMessage={t('events.noPast')} />
          </TabsContent>
          {cancelled.length > 0 && (
            <TabsContent value="cancelled">
              <EventGrid events={cancelled} onEdit={handleEdit} onViewRegistrations={setViewingRegistrations} onRefresh={fetchEvents} emptyMessage="" />
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>
  );
};

function EventGrid({ events, onEdit, onViewRegistrations, onRefresh, emptyMessage }: {
  events: GroupEvent[];
  onEdit: (e: GroupEvent) => void;
  onViewRegistrations: (id: string) => void;
  onRefresh: () => void;
  emptyMessage: string;
}) {
  if (events.length === 0) {
    return <p className="text-center text-muted-foreground py-8">{emptyMessage}</p>;
  }
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mt-4">
      {events.map(event => (
        <EventCard
          key={event.id}
          event={event}
          onEdit={() => onEdit(event)}
          onViewRegistrations={() => onViewRegistrations(event.id)}
          onRefresh={onRefresh}
        />
      ))}
    </div>
  );
}

export default GroupEventsManager;
